import React, { useEffect, useState } from "react";
import { IonButton, IonSpinner } from "@ionic/react";

import { useTranslation } from "react-i18next";
import {
  useCreateTinkoffQRMutation,
  useGetTinkoffPaymentQuery,
  useGetTinkoffPaymentStatus,
} from "../../../../providers/payment";
import { Browser } from "@capacitor/browser";
import { TinkoffPayment, TinkoffPaymentStatus } from "@core/types";
import { toast } from "react-toastify";

interface PaymentProps {
  orderId: string;
  price: number;
  currencySymbol: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

const Payment = ({ orderId }: PaymentProps) => {
  const { t } = useTranslation();

  const getTinkoffPaymentQueryEnabled = !!orderId;

  const [paymentId, setPaymentId] = useState<TinkoffPayment["providerPaymentId"] | undefined>(undefined);
  const [paymentStatus, setPaymentStatus] = useState<TinkoffPaymentStatus | undefined>(undefined);

  const [isPaymentLoading, setIsPaymentLoading] = useState(false);

  const { data: tinkoffPayment, isLoading: isTinkoffPaymentLoading } = useGetTinkoffPaymentQuery({
    orderId,
    options: {
      enabled: getTinkoffPaymentQueryEnabled,
    },
  });

  const { mutateAsync: createPaymentTinkoffQRAsync, isPending: isCreatePaymentTinkoffQRPending } =
    useCreateTinkoffQRMutation();

  const handleCreatePaymentTinkoffQR = async () => {
    setIsPaymentLoading(true);

    try {
      const providerPaymentUrl = tinkoffPayment?.providerPaymentUrl;
      if (providerPaymentUrl) {
        setPaymentId(tinkoffPayment.providerPaymentId);
        await Browser.open({ url: providerPaymentUrl });
      } else {
        const createPaymentTinkoffQRData = await createPaymentTinkoffQRAsync(orderId);
        if (createPaymentTinkoffQRData?.errorCode === "3001") {
          toast.error(`${createPaymentTinkoffQRData?.message}. ${t("Please try again")}`);
          setIsPaymentLoading(false);
          return;
        }

        if (createPaymentTinkoffQRData?.paymentURL) {
          setPaymentId(createPaymentTinkoffQRData.paymentId);
          setPaymentStatus(createPaymentTinkoffQRData.status as TinkoffPaymentStatus);
          await Browser.open({ url: createPaymentTinkoffQRData.paymentURL });
        }
      }
    } catch (error: any) {
      toast.error(`${t("Error processing payment")}: ${error?.message || "Unknown error"}`);
      setIsPaymentLoading(false);
    }
  };

  useEffect(() => {
    if (tinkoffPayment) {
      setPaymentStatus(tinkoffPayment.status);
    }
  }, [tinkoffPayment]);

  useGetTinkoffPaymentStatus(paymentId, paymentStatus, orderId, setPaymentStatus, setIsPaymentLoading);

  if (paymentStatus === "CONFIRMED") {
    return null;
  }

  const isLoading = (paymentStatus && paymentStatus !== "NEW") || isPaymentLoading || isCreatePaymentTinkoffQRPending;

  const isInitiallyLoading = getTinkoffPaymentQueryEnabled && isTinkoffPaymentLoading;
  const isButtonDisabled = isLoading || isInitiallyLoading;

  console.log({ paymentStatus, isLoading, isTinkoffPaymentLoading });

  return (
    <IonButton
      expand="block"
      className="disabled:opacity-40"
      onClick={handleCreatePaymentTinkoffQR}
      disabled={isCreatePaymentTinkoffQRPending || isButtonDisabled}
    >
      {isLoading ? (
        <div className="flex items-center justify-center gap-2">
          <span>{t("WAITING_PAYMENT")}...</span>
          <IonSpinner name="crescent" />
        </div>
      ) : isInitiallyLoading ? (
        <IonSpinner name="crescent" />
      ) : (
        t("Pay Now")
      )}
    </IonButton>
  );
};

export default Payment;
